import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  buildSecurityLabel,
  hydratePortfolio,
  portfolioToObj,
} from "~utils/portfolio"
import { Holdings, PortfolioPayload, Security } from "~shapes/portfolio"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { Form, FormSubmit } from "~styled/form"
import Input from "~app/Input"
import ObjectEditor from "~components/object-editor/ObjectEditor"
import FlexCol from "~components/FlexCol"
import { useFormContext } from "react-hook-form"
import { ERROR_MESSAGES } from "~constants/errorMessages"
import { isEmpty } from "~utils/utils"
import PortfolioService from "~services/PortfolioService"
import ConfirmationModal from "~components/ConfirmationModal"
import { toast } from "react-toastify"
import { useMutation } from "react-query"
import colors from "~constants/colors"

interface Props {
  userId: string
  portfolio: Holdings[]
  showSubmitBtn?: boolean
  isLoading: boolean
  brokerName?: string
  existPortfolio?: boolean
  showPooFields: boolean
  showBrokerMessage?: boolean
  onRefetchPortfolios?: () => void
  disableFields?: boolean
  userName?: string
  userEmail?: string
  month?: number
  year?: number
  aiEnhancedPortfolio?: any[]
  aiFilledFields?: Set<string>
  aiSuggestsReject?: boolean
  aiSuggestsReview?: boolean
  columns?: number
  hideEmpty?: boolean
  securities?: Security[]
  stackLabel?: boolean
  showSecurityCode?: boolean
}

const PortfolioForm = ({
  userId,
  portfolio,
  showSubmitBtn,
  isLoading,
  brokerName,
  existPortfolio,
  showPooFields = false,
  onRefetchPortfolios,
  disableFields = false,
  userName,
  userEmail,
  month,
  year,
  aiEnhancedPortfolio,
  aiFilledFields,
  aiSuggestsReject = false,
  aiSuggestsReview = false,
  columns,
  hideEmpty,
  securities,
  stackLabel = false,
  showSecurityCode = true,
}: Props) => {
  // Seed values used to render the (uncontrolled) holdings inputs. This only
  // changes on a real reseed — never on keystrokes — so typing does not trigger
  // a re-render of the form.
  const [portfolioObj, setPortfolioObj] = React.useState({})
  const [loadingData, setLoadingData] = useState(true)
  // Drives the "clear all holdings" confirmation modal. We use the app modal
  // (not the native confirm) so the prompt matches the rest of the flow and we
  // can wire Enter-to-confirm / Esc-to-cancel shortcuts.
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  // Signature of the seed. Used both to skip identical re-emits and as the
  // ObjectEditor's `key`, so the editor only remounts (and re-applies its
  // defaultValues) on a genuine reseed, not while the user is typing.
  const [seedKey, setSeedKey] = useState<string>("")
  // Content signature of the last portfolio we seeded from. A background
  // re-emit with the same values (refetch, broker auto-select, StrictMode) is
  // ignored so it can never overwrite an in-progress edit.
  const lastInitializedRef = useRef<string | null>(null)
  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    handleSubmit,
    unregister,
  } = useFormContext()
  const refForm = useRef(null)

  const { mutateAsync: mutatePortfolioAsync } = useMutation(
    (payload: PortfolioPayload) => PortfolioService.postUserPortfolio(payload),
    {
      onSuccess: () => {
        toast.success(`Portfolio was successfully created.`)
        // Edit is persisted; allow the post-save refetch to reseed the form
        // with the freshly saved values.
        lastInitializedRef.current = null
        if (onRefetchPortfolios) {
          onRefetchPortfolios()
        }
      },
      onError: () => {
        toast.error(`There was an error saving the portfolio.`)
      },
    },
  )

  const movePropertyToFirst = (obj, propertyNames: string[]) => {
    const orderedProperties = {}
    const restProperties = {}

    Object.entries(obj).map(([key, value]) => {
      if (propertyNames.includes(key)) {
        orderedProperties[key] = value
      } else {
        restProperties[key] = value
      }
    })
    const orderedPropertiesArr = propertyNames.map(key => ({
      [key]: orderedProperties[key],
    }))
    const reorderedProperties = Object.assign({}, ...orderedPropertiesArr)
    const sortedRestProperties = Object.fromEntries(
      Object.entries(restProperties).sort(([keyA], [keyB]) =>
        keyA.localeCompare(keyB),
      ),
    )
    return { ...reorderedProperties, ...sortedRestProperties }
  }

  const priorityCodes = [
    "nyse-ccl-2026",
    "nyse-ccl",
    "lse-ccl",
    "nyse-cuk",
    "nyse-nclh",
    "nyse-rcl",
    "nasdaq-etor",
  ]

  const securityLabels: Record<string, string> = {}
  ;(securities ?? []).forEach(s => {
    securityLabels[s.code] = buildSecurityLabel(s, showSecurityCode)
  })

  // Frequent-action chips ("+100 NYSE.CCL"): the priority securities that the
  // agent actually has available, in priority order. One tap adds a fixed
  // amount to that holding so the common case (CCL-heavy statements) is a click
  // instead of typing a code and a number.
  const QUICK_ADD_AMOUNT = 100
  const quickAddSecurities = priorityCodes
    .map(code => (securities ?? []).find(s => s.code === code))
    .filter((s): s is Security => Boolean(s))

  useEffect(() => {
    register("portfolio")
  }, [])

  // The broker is only editable when creating a portfolio; for an existing one
  // it's locked. The create-mode broker input can transiently register "broker"
  // as required before existPortfolio resolves, and react-hook-form keeps that
  // registration after it unmounts — silently blocking the update submit. Clear
  // it so the locked broker never gates an update.
  useEffect(() => {
    if (existPortfolio) {
      unregister("broker")
    }
  }, [existPortfolio])

  useEffect(() => {
    let portfolioObj = portfolioToObj(portfolio)

    // If we have AI-enhanced portfolio data, merge it with the existing portfolio
    if (aiEnhancedPortfolio && aiEnhancedPortfolio.length > 0) {
      aiEnhancedPortfolio.forEach(item => {
        // Only add positive quantities to the portfolio
        if (item.quantity > 0) {
          portfolioObj[item.security] = item.quantity
        }
      })
    }

    portfolioObj = movePropertyToFirst(portfolioObj, priorityCodes)

    // Only (re)seed the form when the incoming portfolio content actually
    // changes. Without this guard, a benign re-render (refetch, broker watch,
    // StrictMode double-invoke) re-runs this effect with the same values but a
    // fresh reference and resets the editor — silently reverting whatever the
    // user just typed back to the loaded value.
    const signature = JSON.stringify(portfolioObj)
    if (lastInitializedRef.current === signature) {
      setLoadingData(false)
      return
    }
    lastInitializedRef.current = signature

    setPortfolioObj(portfolioObj)
    setSeedKey(signature)
    setLoadingData(false)
    setValue("portfolio", portfolioObj)
  }, [portfolio, aiEnhancedPortfolio])

  // Keystrokes update only the form store (no setState, no re-render). The
  // inputs are uncontrolled, so the DOM holds the typed value and we read the
  // final values from the store on save — nothing can race a keystroke.
  const handleDynamicPortfolioChanges = (data: object) => {
    setValue("portfolio", data)
  }

  // Add `amount` to a holding from a quick-action chip. We read the latest
  // values from the form store (kept in sync on every keystroke), merge the
  // delta, and reseed through the same path the editor uses for AI/portfolio
  // fills — so the uncontrolled inputs remount with the new value while every
  // other in-progress edit is preserved. Adds the row if it didn't exist yet.
  const handleQuickAdd = (code: string, amount: number) => {
    const current = (getValues("portfolio") as Record<string, any>) ?? {}
    const nextValue = (Number(current[code]) || 0) + amount
    const next = movePropertyToFirst(
      { ...current, [code]: nextValue },
      priorityCodes,
    )

    const signature = JSON.stringify(next)
    // Keep the seed guard in sync so a benign re-emit can't revert this add.
    lastInitializedRef.current = signature
    setPortfolioObj(next)
    setSeedKey(signature)
    setValue("portfolio", next)
  }

  // Open the confirmation modal (no-op if there's nothing to clear).
  const handleClearAll = () => {
    const current = (getValues("portfolio") as Record<string, any>) ?? {}
    if (isEmpty(current)) return
    setShowClearConfirm(true)
  }

  // Reset every holding back to empty so the user can re-enter from scratch.
  // We keep the rows (set each value to "" rather than dropping keys) and
  // reseed through the same remount path as quick-add/AI fills, so the
  // uncontrolled inputs clear visually.
  const confirmClearAll = () => {
    const current = (getValues("portfolio") as Record<string, any>) ?? {}
    const cleared = Object.keys(current).reduce(
      (acc, code) => {
        acc[code] = ""
        return acc
      },
      {} as Record<string, any>,
    )

    const signature = JSON.stringify(cleared)
    // Keep the seed guard in sync so a benign re-emit can't revert the clear.
    lastInitializedRef.current = signature
    setPortfolioObj(cleared)
    setSeedKey(signature)
    setValue("portfolio", cleared)
    setShowClearConfirm(false)
  }

  // While the clear-confirm modal is open it owns the keyboard: Enter confirms.
  // Esc-to-cancel is handled by the modal itself (react-modal onRequestClose).
  //
  // Listen on the capture phase and stopImmediatePropagation so this Enter is
  // fully consumed here. The host page (create-portfolio) has its own window
  // keydown listener that treats Enter as "validate"; it's registered first, so
  // without capturing + stopping, confirming the clear with Enter would also
  // pop the Validate confirmation modal.
  useEffect(() => {
    if (!showClearConfirm) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        e.stopImmediatePropagation()
        confirmClearAll()
      }
    }
    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [showClearConfirm])

  const isAiFilled = (fieldName: string) => {
    return aiFilledFields && aiFilledFields.has(fieldName)
  }

  const getAiHighlightClass = (fieldName: string) => {
    if (!isAiFilled(fieldName)) return ""
    if (aiSuggestsReject) return "ai-filled-field ai-filled-field--reject"
    if (aiSuggestsReview) return "ai-filled-field ai-filled-field--review"
    return "ai-filled-field"
  }

  const handleSavePortfolio = async (): void => {
    // The form store is kept in sync on every keystroke via setValue, so it
    // holds exactly what the user typed — read the holdings from there.
    const finalPortfolio = getValues("portfolio") ?? {}

    if (isEmpty(finalPortfolio)) return
    const holdings = hydratePortfolio(finalPortfolio, securities)
    if (
      confirm(
        "This action can't be reverted, are you sure you want to create this portfolio?",
      )
    ) {
      const values = getValues()
      const brokerValue =
        values.broker || values.selected_broker?.value || brokerName || ""

      const requestBody = {
        userId: userId,
        broker: brokerValue,
        holdings: holdings,
      }
      await mutatePortfolioAsync(requestBody)
    }
  }

  // Surface validation failures instead of silently swallowing the submit.
  const handleInvalidSubmit = (formErrors: object) => {
    const fields = Object.keys(formErrors)
    if (fields.length > 0) {
      toast.error(`Please complete required fields: ${fields.join(", ")}`)
    }
  }

  const validate = month => {
    const year = getValues("year")
    if (!year) return "Please enter the year first"
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1
    const inputDate = new Date(year, month - 1)
    const comparisonDate = new Date(currentYear, currentMonth - 4)
    if (inputDate < comparisonDate) {
      return "The statement date cannot be more than 3 months in the past."
    } else {
      return true
    }
  }

  return (
    <>
      <ConfirmationModal
        isOpen={showClearConfirm}
        title="Clear all holdings"
        message="This clears every share input so you can re-enter from scratch. Any values you've typed will be lost."
        confirmLabel="Clear all"
        shortcutHint
        onConfirm={confirmClearAll}
        onClose={() => setShowClearConfirm(false)}
      />
      <SkeletonWrapper
        height={20}
        isLoading={isLoading || loadingData}
        count={10}
      >
        <CompactForm
          onSubmit={handleSubmit(handleSavePortfolio, handleInvalidSubmit)}
          ref={refForm}
        >
          {showPooFields && (
            <StatementSection>
              <SectionLabel>Statement Details</SectionLabel>
              {userEmail && <EmailBadge>{userEmail}</EmailBadge>}
              <Input
                name="name"
                placeholder="Holder name"
                label="Name"
                register={register("name", { required: true })}
                errorMessage={errors.name && ERROR_MESSAGES.FIELD_REQUIRED}
                defaultValue={userName}
                className={getAiHighlightClass("name")}
              />
              <Input
                name="broker"
                placeholder="Broker"
                label="Broker"
                disabled={existPortfolio}
                register={register("broker", { required: true })}
                errorMessage={errors.broker && ERROR_MESSAGES.FIELD_REQUIRED}
                defaultValue={brokerName}
                className={getAiHighlightClass("broker")}
              />
              {existPortfolio && (
                <InfoNote>
                  Broker name is locked because you're editing an existing
                  portfolio. Keep this in mind when totaling shares.
                </InfoNote>
              )}
              <FieldRow>
                <FlexCol>
                  <Input
                    name="month"
                    placeholder="MM"
                    label="Month"
                    register={register("month", {
                      required: ERROR_MESSAGES.FIELD_REQUIRED,
                      validate: value => validate(value),
                    })}
                    type={"number"}
                    min={1}
                    max={12}
                    errorMessage={errors.month && errors.month.message}
                    defaultValue={month}
                    className={getAiHighlightClass("month")}
                  />
                </FlexCol>
                <FlexCol>
                  <Input
                    name="year"
                    placeholder="YYYY"
                    label="Year"
                    register={register("year", { required: true })}
                    type={"number"}
                    min={2024}
                    errorMessage={errors.year && ERROR_MESSAGES.FIELD_REQUIRED}
                    defaultValue={year}
                    className={getAiHighlightClass("year")}
                  />
                </FlexCol>
              </FieldRow>
            </StatementSection>
          )}

          {!showPooFields && !existPortfolio && (
            <Input
              name="broker"
              placeholder="Broker"
              label="Broker"
              disabled={existPortfolio}
              register={register("broker", { required: true })}
              errorMessage={errors.broker && ERROR_MESSAGES.FIELD_REQUIRED}
              defaultValue={brokerName}
              className={getAiHighlightClass("broker")}
            />
          )}

          <FormSection>
            <HoldingsHeader>
              <SectionLabel>Holdings</SectionLabel>
              {!disableFields && Object.keys(portfolioObj).length > 0 && (
                <ClearAllButton
                  type="button"
                  onClick={handleClearAll}
                  title="Clear all share inputs"
                >
                  Clear all
                </ClearAllButton>
              )}
            </HoldingsHeader>
            {!disableFields && quickAddSecurities.length > 0 && (
              <QuickAdd>
                <QuickAddTrigger type="button">
                  Quick add
                  <Count>{quickAddSecurities.length}</Count>
                  <Caret aria-hidden>▾</Caret>
                </QuickAddTrigger>
                <QuickAddFlyout>
                  {quickAddSecurities.map(sec => (
                    <QuickAddChip
                      key={sec.code}
                      type="button"
                      onClick={() => handleQuickAdd(sec.code, QUICK_ADD_AMOUNT)}
                      title={`Add ${QUICK_ADD_AMOUNT} shares to ${buildSecurityLabel(
                        sec,
                        true,
                      )}`}
                    >
                      +{QUICK_ADD_AMOUNT} {buildSecurityLabel(sec, false)}
                    </QuickAddChip>
                  ))}
                </QuickAddFlyout>
              </QuickAdd>
            )}
            <ObjectEditor
              key={seedKey}
              data={portfolioObj}
              onChange={handleDynamicPortfolioChanges}
              type="number"
              disabled={disableFields}
              aiFilledFields={aiFilledFields}
              aiSuggestsReject={aiSuggestsReject}
              aiSuggestsReview={aiSuggestsReview}
              columns={columns}
              hideEmpty={hideEmpty}
              labels={securityLabels}
              stackLabel={stackLabel}
            />
          </FormSection>

          {showSubmitBtn && (
            <FormSubmit type="submit" disabled={isEmpty(portfolioObj)}>
              {existPortfolio ? "Update Portfolio" : "Save Portfolio"}
            </FormSubmit>
          )}
        </CompactForm>
      </SkeletonWrapper>
    </>
  )
}

const CompactForm = styled(Form)`
  > * {
    margin-bottom: 0;
  }
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  &:not(:first-child) {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${colors.gray9};
  }
`

// Compact variant for the Statement Details fields so the Holdings section sits
// higher (less scrolling to reach the pre-filled holdings). Scoped overrides on
// the shared Input — :has(> .label) targets the InputContainer wrappers only,
// not the FieldRow/FlexCol layout divs.
const StatementSection = styled(FormSection)`
  div:has(> .label) {
    margin-bottom: 8px;
  }
  .label {
    margin-bottom: 2px;
  }
  input {
    height: 30px;
  }
  /* Drop the always-rendered empty helper line that pads every field. */
  .info-copy,
  .input-limit {
    display: none;
  }
  .error-message {
    margin-top: 2px;
  }
`

const SectionLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${colors.gray6};
  margin-bottom: 6px;
`

const EmailBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.blue1};
  background: ${colors.gray5};
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
  word-break: break-all;
`

const InfoNote = styled.p`
  font-size: 11px;
  color: ${colors.gray6};
  line-height: 1.4;
  margin: -14px 0 12px;
  padding: 6px 8px;
  background: ${colors.gray1};
  border-radius: 4px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 8px;
`

const HoldingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const ClearAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.gray6};
  background: transparent;
  border: 1px solid ${colors.gray9};
  border-radius: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.15s,
    background 0.15s,
    border-color 0.15s;

  &:hover {
    color: ${colors.red};
    background: ${colors.gray1};
    border-color: ${colors.red};
  }

  &:active {
    background: ${colors.gray5};
  }
`

// Collapsed by default to save vertical space; the chips reveal in a flyout on
// hover or keyboard focus (absolutely positioned, so they overlay rather than
// pushing the Holdings grid down).
const QuickAdd = styled.div`
  position: relative;
  width: fit-content;
  margin-bottom: 8px;
`

const QuickAddTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.gray6};
  background: ${colors.gray1};
  border: 1px solid ${colors.gray9};
  border-radius: 11px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.blue2};
  }
`

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 700;
  color: ${colors.blue1};
  background: ${colors.gray9};
  border-radius: 8px;
`

const Caret = styled.span`
  font-size: 9px;
  line-height: 1;
`

const QuickAddFlyout = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  display: none;
  flex-wrap: wrap;
  gap: 6px;
  width: max-content;
  max-width: 360px;
  /* No gap to the trigger: a gap would drop :hover before reaching the flyout.
     A transparent top padding gives breathing room while keeping the hover
     bridge continuous. */
  padding: 10px 8px 8px;
  background: white;
  border: 1px solid ${colors.gray9};
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);

  /* Hover-only: the flyout closes as soon as the pointer leaves the Quick add
     group. (No :focus-within — that would keep it open after a chip click or
     keyboard focus, even once the mouse has moved away.) */
  ${QuickAdd}:hover & {
    display: flex;
  }
`

const QuickAddChip = styled.button`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.blue1};
  background: ${colors.gray1};
  border: 1px solid ${colors.gray9};
  border-radius: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: ${colors.gray5};
    border-color: ${colors.blue2};
  }

  &:active {
    background: ${colors.gray9};
  }
`

export default PortfolioForm
